/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    AlertItemDto,
    AlertDto,
    ALERT_STATUSES,
    AlertTypeDto,
    ENTITY_TYPES,
    ALERT_PRIORITIES,
    AlertCashBoxItemDto,
    AlertDocumentItemDto,
    AlertCompanyItemDto,
    AlertStoreItemDto,
    EntityType,
    AlertName,
    ALERT_NAME,
    AlertTemplateDTO,
    ALERT_TEMPLATE_GROUPS,
    PERIODS,
    AlertTemplateGroup,
    TemplateExecutionDTO,
    TEMPLATES_EXECUTION_STATES,
    TemplateRuleDto,
} from './alertsClient.typings';
import { factoryT, fields } from 'factory-t';
import { StoreType } from '../stores/storesClientTypings';
import { detailedUserFactory } from '../users/usersFactories';
import { factoryTBuilder } from 'factory-t/build';
import moment from 'moment';
import { ShortUserDto } from '../users/usersClient.typings';

export const alertDtoFactory = factoryT<AlertDto>({
    id: fields.index(),
    status: fields.sequence([...ALERT_STATUSES]),
    itemsCount: (ctx) => ctx.index % 16 || 1,
    inserted: 1564736104814,
    type: () => alertTypeDtoFactory.item(),
    assigneeUser: () => {
        const user = detailedUserFactory.item();

        const userForAlert: ShortUserDto = {
            ...user,
            id: Number(user.lastName.split(' ')[1]),
        };

        return userForAlert;
    },
});

const AlertNamesByType: Record<EntityType, AlertName[]> = {
    DOCUMENT: [
        'INVALID_DOCUMENT_REFERENCE_ALERT',
        'CREATION_DATE_IS_AFTER_THAN_INSERTION_DATE_ALERT',
        'WRONG_VAT_PERCENT_ALERT',
    ],
    CASH_BOX: [
        'CASH_BOX_WITHOUT_AUDIT_ALERT',
        'CASH_BOX_WITH_INVALID_TIME_ALERT',
        'DOCUMENTS_CHAIN_GAP_ALERT',
        'CASH_BOX_WITH_FAILED_AUDIT_ALERT',
        'CASH_BOX_CERTIFICATE_IS_EXPIRED_ALERT',
        'ACTIVE_CASH_BOX_WITHOUT_DOCS_ALERT',
        'CASHBOX_WITH_INVALID_GPS_ALERT',
    ],
    STORE: [
        'ACTIVE_STORE_WHERE_ALL_CASH_BOXES_BLOCKED_ALERT',
        'INACTIVE_STORE_WITH_ACTIVE_CASH_BOX_ALERT',
        'STORE_WITH_INVALID_GPS_ALERT',
    ],
    COMPANY: ['ACTIVE_TAXPAYER_WITH_NO_ACTIVE_CASH_BOX_ALERT', 'CLOSED_TAXPAYER_WITH_ACTIVE_CASH_BOX_ALERT'],
};

export const alertTypeDtoFactory = factoryTBuilder<AlertTypeDto>({
    entityType: fields.sequence([...ENTITY_TYPES]),
    name: fields.sequence([...ALERT_NAME]),
    priority: fields.sequence([...ALERT_PRIORITIES]),
})
    .setFieldFactory('name', (ctx) => {
        return AlertNamesByType[ctx.inject('entityType')][
            Math.floor(Math.random() * AlertNamesByType[ctx.inject('entityType')].length)
        ];
    })
    .factory();

export const alertItemsDtoFactory = factoryT<AlertItemDto>({
    id: fields.index(),
    alertId: 1234545,
    ignore: fields.sequence([false, false, false, false, true]),
    ignoreReason: fields.string(),
    content: null,
});

export const alertItemContentFactoryByType = {
    CASH_BOX: factoryT<AlertCashBoxItemDto['content']>({
        cashBoxTaxNumber: (ctx) => `765432${ctx.index}`,
        storeTaxNumber: '1234567',
        storeName: (ctx) => `storeName-${ctx.index}`,
        storeType: fields.sequence(Object.values(StoreType)),
        extended: {
            lastDocCreatedAtUtc: '0',
            lastDocInsertedAtUtc: '0',
            lastOnlineAtUtc: '1627921617',
            timeDelta: '-358',
        },
    }),
    DOCUMENT: factoryT<AlertDocumentItemDto['content']>({
        storeTaxNumber: '1234567',
        storeName: (ctx) => `storeName-${ctx.index}`,
        storeType: fields.sequence(Object.values(StoreType)),
        docId: '8WY4UxTPCLm1bcCc2b8CXsjb7Hox9rBXXJ9r4kG3Ky5e',
        createdAtUtc: 10005000,
        extended: {
            referencedDocId: '1E5aMGdpHU5BnmkBqbr94g24EevWcgQGLXURVf8YL2cLs',
            sum: '5.01',
        },
    }),
    COMPANY: factoryT<AlertCompanyItemDto['content']>({
        companyTaxNumber: '1234567',
        companyName: (ctx) => `companyName-${ctx.index}`,
        extended: {
            cashBoxRegDateAtUtc: '1626337813',
        },
    }),
    STORE: factoryT<AlertStoreItemDto['content']>({
        storeTaxNumber: '1234567',
        storeName: (ctx) => `storeName-${ctx.index}`,
        storeType: fields.sequence(Object.values(StoreType)),
        extended: {
            cashBoxRegDateAtUtc: '1626329250',
        },
    }),
} as const;

// ALERTS TEMPLATES

export const AlertNamesByTemplateGroup: Record<AlertTemplateGroup, AlertName[]> = {
    period: ['INVALID_DOCUMENT_REFERENCE_ALERT', 'CREATION_DATE_IS_AFTER_THAN_INSERTION_DATE_ALERT'],
    status: [
        'CASH_BOX_WITH_INVALID_TIME_ALERT',
        'CASH_BOX_CERTIFICATE_IS_EXPIRED_ALERT',
        'CASH_BOX_WITH_FAILED_AUDIT_ALERT',
        'CASH_BOX_WITHOUT_AUDIT_ALERT',
        'ACTIVE_TAXPAYER_WITH_NO_ACTIVE_CASH_BOX_ALERT',
        'CLOSED_TAXPAYER_WITH_ACTIVE_CASH_BOX_ALERT',
        'ACTIVE_CASH_BOX_WITHOUT_DOCS_ALERT',
        'CASHBOX_WITH_INVALID_GPS_ALERT',
        'STORE_WITH_INVALID_GPS_ALERT',
    ],
};

export const alertTemplateFactory = factoryTBuilder<AlertTemplateDTO>({
    description: 'Check cash boxes with invalid time',
    enabled: true,
    group: fields.sequence([...ALERT_TEMPLATE_GROUPS]),
    id: fields.index(),
    key: fields.sequence([...ALERT_NAME]),
    schedule: fields.sequence([
        '0 0 6 ? * *',
        '0 0 0 ? * MON *',
        '0 30 0 1 JAN-DEC ? *',
        '0 0 4 1/1 * ? *',
        '0 0 1 1/1 * ? *',
        '0 15 0 ? * MON *',
        '0 0 0 1/1 * ? *',
        '0 0 1 ? * MON *',
        '0 0 2 1 JAN-DEC ? *',
        null,
        '',
        undefined,
    ]),
    period: fields.sequence([...PERIODS]),
    assigneeUser: () => {
        const user = detailedUserFactory.item();
        const userForAlert: ShortUserDto = {
            ...user,
            id: Number(user.lastName.split(' ')[1]),
        };

        return userForAlert;
    },
    alertEntityType: fields.sequence([...ENTITY_TYPES]),
})
    .setFieldFactory('group', (ctx) =>
        AlertNamesByTemplateGroup.period.includes(ctx.inject('key'))
            ? ALERT_TEMPLATE_GROUPS[0]
            : ALERT_TEMPLATE_GROUPS[1],
    )
    .factory();

export const alertTemplateExecution = factoryT<TemplateExecutionDTO>({
    duration: '0:14:24',
    id: (ctx) => ctx.index,
    parameters: {
        start: '',
        end: '',
    },
    regular: true,
    retriesCount: fields.index(),
    started: '2021-09-13T02:00:00.398',
    state: TEMPLATES_EXECUTION_STATES[3],
});

export const alertTemplateRuleFactory = factoryT<Pick<TemplateRuleDto, 'id' | 'insertedAtUtc'>>({
    id: (ctx) => ctx.index,
    insertedAtUtc: moment(new Date()).utc().format(),
});
